import OneSignal from "react-onesignal";
import { fetchNotifications } from "src/stores/notification";
import { store } from "../stores";
import { addReminderToast } from "src/stores/reminderToasts/slice";
import sound from "./notification-sound.mp3";
// import { setAlert } from "src/stores/basicSlices";
import { fetchTicketQueues } from "src/stores/ticketQueue";
import { addCallLogsToast } from "src/stores/callLogsToasts/slice";
import {
  setHasDeviceToken,
  setPermissionStatus,
} from "../stores/notificationPermission";
import { fetchQueues } from "src/stores/queue";

export default async function runOneSignal() {
  try {
    const stored = localStorage.getItem("deviceToken");
    const appId: any =
      process.env.REACT_APP_ONESIGNAL_APP_ID ||
      "f7d70ef2-7f06-4efe-a1f5-ad9b77ce7870";

    // Always init OneSignal first
    await OneSignal.init({
      appId,
      allowLocalhostAsSecureOrigin: true,
    });

    // Listen for permission granted event from modal
    const handlePermissionGranted = async () => {
      // Re-run initialization when permission is granted
      setTimeout(async () => {
        await runOneSignal();
      }, 1000);
    };

    // Remove existing listener to avoid duplicates
    window.removeEventListener(
      "notification-permission-granted",
      handlePermissionGranted,
    );
    window.addEventListener(
      "notification-permission-granted",
      handlePermissionGranted,
    );

    // Determine permission and request if needed
    let permission: NotificationPermission = Notification.permission;

    if (permission !== "granted" && permission !== "denied") {
      try {
        // Prefer OneSignal API (falls back to Notification API internally)
        // @ts-ignore - typings can differ between SDK versions
        const result: any = await (
          OneSignal as any
        ).Notifications?.requestPermission?.();
        if (typeof result === "string") {
          permission = result as NotificationPermission;
        } else {
          // Fallback to standard API
          permission = await Notification.requestPermission();
        }
      } catch {
        permission = await Notification.requestPermission();
      }
    }

    if (permission === "denied") {
      localStorage.removeItem("deviceToken");
      store.dispatch(setHasDeviceToken(false));
      store.dispatch(setPermissionStatus("denied"));
      return null;
    }

    if (permission === "granted") {
      try {
        // Ensure we are subscribed to push
        // @ts-ignore - SDK v16 exposes subscribe()
        if ((OneSignal as any).User?.PushSubscription?.optedIn === false) {
          await (OneSignal as any).User?.PushSubscription?.subscribe?.();
        }
      } catch {}

      // Try to get a stable identifier: PushSubscription id, then OneSignal user id
      // @ts-ignore
      let token: string | null | undefined = (OneSignal as any).User
        ?.PushSubscription?.id;
      // console.log("token000>", token);
      try {
        if (!token && (OneSignal as any).User?.getId) {
          token = await (OneSignal as any).User.getId();
        }
      } catch {}

      if (token && `${token}`.trim()) {
        localStorage.setItem("deviceToken", `${token}`);
        store.dispatch(setHasDeviceToken(true));
        store.dispatch(setPermissionStatus("granted"));
        return token as string;
      }

      // Fallback to previously stored value if available
      if (stored && `${stored}`.trim()) {
        store.dispatch(setHasDeviceToken(true));
        store.dispatch(setPermissionStatus("granted"));
        return stored;
      }

      store.dispatch(setHasDeviceToken(false));
      return null;
    }

    // If permission is default (neither granted nor denied), return stored value if any
    store.dispatch(setPermissionStatus("default"));
    if (stored && `${stored}`.trim()) {
      store.dispatch(setHasDeviceToken(true));
      return stored;
    }

    store.dispatch(setHasDeviceToken(false));
    return stored;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const usePushNotification = () => {
  async function requestPermission() {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      let workerRegistered = false;

      for (const registration of registrations) {
        if (
          registration.active &&
          registration.active.scriptURL.includes("OneSignalSDKWorker.js")
        ) {
          workerRegistered = true;
          break;
        }
      }
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        if (!workerRegistered) {
          if ("serviceWorker" in navigator) {
            const registration = await navigator.serviceWorker.register(
              "/OneSignalSDKWorker.js",
            );
            console.log("Service Worker Registered");
          }
        }
      }
    } catch (error) {
      console.error(
        "Error during permission request or service worker registration:",
        error,
      );
    }
  }

  requestPermission();
  const notificationHandler = () => {
    OneSignal.Notifications.addEventListener(
      "foregroundWillDisplay",
      (event: any) => {
        const isLoggedIn = store?.getState().authUser?.token;

        if (isLoggedIn) {
          new Audio(sound).play();

          const type =
            event?.notification?.additionalData?.eventType ||
            event?.additionalData?.eventType ||
            event?.meta?.raw?.notification?.additionalData?.eventType ||
            event?.data?.eventType;

          // Check for call_queue_reminder notifications (pending feedback reminders)
          const notificationType =
            event?.notification?.additionalData?.notification_type ||
            event?.additionalData?.notification_type;

          console.log(event, "eventeventeventeventevent");

          if (notificationType === "call_queue_reminder") {
            console.log(
              "Call queue reminder detected! Type:",
              notificationType,
            );
            const genCalllogsId = () => {
              try {
                if (
                  typeof crypto !== "undefined" &&
                  (crypto as any).randomUUID
                ) {
                  return (crypto as any).randomUUID();
                }
              } catch (e) {}
              return `reminder-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 8)}`;
            };
            const reminderPayload = {
              id: genCalllogsId(),
              title: event?.notification?.title || "Call Feedback Reminder",
              description:
                event?.notification?.body || "You have pending call feedback",
              createdAt: Date.now(),
              meta: { raw: event },
            };

            store?.dispatch(addCallLogsToast(reminderPayload as any));
            console.debug(
              "OneSignal: dispatched call_queue_reminder toast, store.callLogsToasts:",
              store.getState()?.callLogsToasts,
            );
            return;
          } else if (type === "call_inbound" || type === "call_outbound") {
            console.log("Call notification detected! Type:", type);
            const genCalllogsId = () => {
              try {
                if (
                  typeof crypto !== "undefined" &&
                  (crypto as any).randomUUID
                ) {
                  return (crypto as any).randomUUID();
                }
              } catch (e) {}
              return `calllogs-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 8)}`;
            };
            const newPayload = {
              id: genCalllogsId(),
              title: event?.notification?.title,
              description: event?.notification?.description,
              createdAt: Date.now(),
              meta: { raw: event },
            };

            store?.dispatch(addCallLogsToast(newPayload as any));
            // Debug: log current store value to ensure reducer ran
            try {
              // eslint-disable-next-line no-console
              console.debug(
                "OneSignal: dispatched addCallLogsToast, store.callLogsToasts:",
                store.getState()?.callLogsToasts,
              );
            } catch (e) {
              // ignore
            }
            (window as any).__addCallLogsToastFromEvent = (
              id: string,
              p: any,
            ) => {
              const item = { id, ...p, createdAt: Date.now() };
              return store?.dispatch(addCallLogsToast(item as any));
            };
            return;
          } else {
            store?.dispatch(fetchNotifications());
            store?.dispatch(
              fetchTicketQueues({ page: 1, limit: 10, reset: true }),
            );
            store?.dispatch(fetchQueues({}))
            // Persistent reminder toast
            try {
              const genId = () => {
                try {
                  if (
                    typeof crypto !== "undefined" &&
                    (crypto as any).randomUUID
                  ) {
                    return (crypto as any).randomUUID();
                  }
                } catch (e) {}
                return `reminder-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2, 8)}`;
              };

              const payload = {
                id: genId(),
                title: event?.notification?.title,
                description: event?.notification?.body,
                createdAt: Date.now(),
                meta: { raw: event },
              };

              store?.dispatch(addReminderToast(payload as any));

              (window as any).__addReminderToastFromEvent = (
                id: string,
                p: any,
              ) => {
                const item = { id, ...p, createdAt: Date.now() };
                return store?.dispatch(addReminderToast(item as any));
              };
            } catch (err) {}
          }
        } else {
          console.log("User is not logged in. Skipping notification.");
        }
      },
    );
  };

  notificationHandler();
};
